import { ChevronRightIcon } from "@chakra-ui/icons";
import { Box, Flex, Text } from "@chakra-ui/react";
import React from "react";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import { usePremium } from "../../../contexts/Premium";
import { trackEvent } from "../../../shared/analytics";
import BrowseSectionTitle from "./BrowseSectionTitle";
import {
  discoverQueryKey,
  discoverQueryOptions,
  getDiscoverItems,
} from "../helpers/discoverSections";
import DiscoverCard, { DISCOVER_CARD_RATIO } from "./DiscoverCard";

const CARD_GAP = 12;
const MAX_CARD_WIDTH = 320;
const PLACEHOLDER_COUNT = 3;
const placeholders = Array.from({ length: PLACEHOLDER_COUNT }, (_, i) => i);

// Same sizing as the app: ~74% of the viewport on phones, capped at 320px.
const getCardWidth = () =>
  Math.min(Math.round(window.innerWidth * 0.74), MAX_CARD_WIDTH);

const useCardWidth = () => {
  const [cardWidth, setCardWidth] = React.useState(getCardWidth);

  React.useEffect(() => {
    const onResize = () => setCardWidth(getCardWidth());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return cardWidth;
};

/**
 * A swipeable row of reading orders (popular / trending / latest) with a
 * "View all" link to the full list. Hides itself when there is nothing to show.
 * Mirrors the mobile DiscoverRow component.
 */
const DiscoverRow = ({ section }) => {
  const { isPremium } = usePremium();
  const cardWidth = useCardWidth();
  const cardHeight = Math.round(cardWidth / DISCOVER_CARD_RATIO);

  const query = useQuery(
    discoverQueryKey(section.key),
    section.fetch,
    discoverQueryOptions
  );
  const items = React.useMemo(() => getDiscoverItems(query.data), [query.data]);

  if (query.isError || (!query.isLoading && !items.length)) {
    return null;
  }

  return (
    <Box as="section" aria-label={section.title}>
      {/* Same eyebrow and spacing as the category sections below the rows */}
      <Flex alignItems="center" justifyContent="space-between" mt="6" mb="2">
        <BrowseSectionTitle my="0">{section.eyebrow}</BrowseSectionTitle>
        {!query.isLoading && (
          <Flex
            as={Link}
            to={`/browse/discover/${section.key}`}
            aria-label={`View all ${section.title.toLowerCase()}`}
            alignItems="center"
            color="#A8B2C8"
            fontSize="12px"
            lineHeight="16px"
            fontWeight="600"
            _hover={{ opacity: 0.7 }}
            onClick={() =>
              trackEvent("browse_discover_view_all_click", {
                section: section.key,
              })
            }
          >
            <Text as="span">View all</Text>
            <ChevronRightIcon boxSize="16px" />
          </Flex>
        )}
      </Flex>
      {query.isLoading ? (
        <Flex gap={`${CARD_GAP}px`} overflow="hidden">
          {placeholders.map((index) => (
            <Box
              key={index}
              flexShrink={0}
              w={`${cardWidth}px`}
              h={`${cardHeight}px`}
              borderRadius="12px"
              bg="#1E2540"
            />
          ))}
        </Flex>
      ) : (
        <Flex
          role="list"
          gap={`${CARD_GAP}px`}
          overflowX="auto"
          scrollSnapType="x mandatory"
          // Give focus rings and the hover lift room without clipping.
          py="2px"
          css={{
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {items.map((item, index) => (
            <Box role="listitem" key={`${item.id ?? "order"}-${index}`}>
              <DiscoverCard
                readingOrder={item}
                width={cardWidth}
                isPremium={isPremium}
                onClick={() =>
                  trackEvent("browse_discover_card_click", {
                    section: section.key,
                    readingOrderId: item.id,
                    position: index + 1,
                  })
                }
              />
            </Box>
          ))}
        </Flex>
      )}
    </Box>
  );
};

export default DiscoverRow;
